import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Wallet, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b'];

export default function NetWorthTracker({ user, onLogout }) {
  const [netWorth, setNetWorth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNetWorth();
  }, []);

  const fetchNetWorth = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/net-worth`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNetWorth(response.data);
    } catch (error) {
      console.error('Error fetching net worth:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <Navigation user={user} onLogout={onLogout} currentPage="/net-worth" />
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Calculating net worth...</p>
          </div>
        </div>
      </div>
    );
  }

  const assetsData = netWorth?.assets_breakdown
    ? Object.entries(netWorth.assets_breakdown).map(([type, value]) => ({
        name: type.charAt(0).toUpperCase() + type.slice(1),
        value: value
      }))
    : [];

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/net-worth" />

        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Net Worth Tracker</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">Track your total wealth and financial health</p>
        </div>

        {/* Net Worth Summary */}
        <Card className="glass p-6 md:p-10 mb-8 dark:bg-gray-800">
          <div className="text-center">
            <h2 className="text-lg text-gray-600 dark:text-gray-400 mb-4">Your Net Worth</h2>
            <p className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 whitespace-nowrap overflow-x-auto px-2 ${netWorth?.net_worth >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {formatCurrency(netWorth?.net_worth || 0, user?.currency)}
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center w-full md:w-auto">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Assets</p>
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                {formatCurrency(netWorth?.total_assets || 0, user?.currency)}
              </p>
            </div>
            
            <div className="hidden md:block text-gray-400 dark:text-gray-500 text-4xl font-light">−</div>
            
            <div className="text-center w-full md:w-auto">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Liabilities</p>
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-rose-600 dark:text-rose-400 whitespace-nowrap">
                {formatCurrency(netWorth?.total_liabilities || 0, user?.currency)}
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Assets Breakdown */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Assets Breakdown</h2>
            {assetsData.length > 0 && netWorth?.total_assets > 0 ? (
              <>
                <div className="flex justify-center mb-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={assetsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {assetsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value, user?.currency)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  {assetsData.map((asset, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-white dark:bg-gray-700 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="text-gray-700 dark:text-gray-300 font-medium">{asset.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 block">{formatCurrency(asset.value, user?.currency)}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {((asset.value / netWorth.total_assets) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">No assets data available</p>
            )}
          </Card>

          {/* Financial Health */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Financial Health</h2>
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-700 p-5 rounded-xl">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-700 dark:text-gray-300 font-medium text-lg">Assets Ratio</span>
                  <span className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{netWorth?.assets_percentage?.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-4">
                  <div 
                    className="bg-gradient-to-r from-emerald-400 to-teal-500 h-4 rounded-full transition-all duration-500"
                    style={{ width: `${netWorth?.assets_percentage || 0}%` }}
                  ></div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 p-5 rounded-xl border-l-4 border-emerald-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500 dark:bg-emerald-600 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Assets</span>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 text-right">{formatCurrency(netWorth?.total_assets || 0, user?.currency)}</p>
                </div>
                
                <div className="bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/20 p-5 rounded-xl border-l-4 border-rose-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-rose-500 dark:bg-rose-600 flex items-center justify-center">
                        <TrendingDown className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Liabilities</span>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-rose-600 dark:text-rose-400 text-right">{formatCurrency(netWorth?.total_liabilities || 0, user?.currency)}</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Financial Health Status
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {netWorth?.net_worth > 0 
                    ? "You're building wealth! Keep up the good work."
                    : "Focus on increasing assets and reducing liabilities."}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
